import {
  FILE_RECORD_MAX_SIZE,
  exceedsFileRecordMaxSize,
} from "@/constants/file-limits";
import { cn } from "@/utils";
import { AttachedFile } from "@cohesive/api-client";
import { FileTypeMark, tintForExt } from "@cohesive/ui-components";
import { AlertTriangle, X } from "lucide-react";
import React, { useEffect, useState } from "react";

interface MessageInputFilePreviewProps {
  file: AttachedFile;
  onRemoveFile?: (file: AttachedFile) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * Composer attachment chip per the design system's chat kit: file-type mark +
 * name + mono size meta + remove. Upload status replaces the mark with a
 * pulsing placeholder; size/upload errors tint the chip.
 */
export const MessageInputFilePreview = React.memo(
  ({ file, onRemoveFile }: MessageInputFilePreviewProps) => {
    const isImage = file.content_type.startsWith("image");
    const fileExtension = file.name.split(".").pop()?.toUpperCase() || "FILE";
    const isUploadInFlight =
      file.status === "pending" || file.status === "initializing";
    const baseName = file.name.includes(".")
      ? file.name.substring(0, file.name.lastIndexOf("."))
      : file.name;

    // Surface the same per-file cap that `validateChatMessageFiles` uses —
    // file-records larger than `FILE_RECORD_MAX_SIZE` will fail the chat
    // message's pre-send validation and the S3 presign policy server-side.
    const fileSize = file.file?.size || file.size || 0;
    const hasSizeError =
      fileSize > 0 && exceedsFileRecordMaxSize({ size: fileSize });
    const maxSizeMB = Math.round(FILE_RECORD_MAX_SIZE / 1024 / 1024);
    const hasError = hasSizeError || file.status === "failed";

    // Mint one blob URL per underlying File, inside an effect so the cleanup
    // can revoke it safely (StrictMode-proof; see git history for the
    // streaming scroll-loop this prevents). An attached existing record's
    // File is a zero-byte placeholder — blob-URLing it renders a broken
    // image, so the server preview (file.url) wins and empty files fall
    // through to the type mark.
    const [blobUrl, setBlobUrl] = useState<string>("");
    useEffect(() => {
      if (!file.file || file.file.size === 0 || !isImage) {
        setBlobUrl("");
        return;
      }
      const url = URL.createObjectURL(file.file);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    }, [file.file, isImage]);
    const previewUrl = file.url || blobUrl;

    return (
      <span
        className={cn(
          "inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md bg-n-05 py-1.5 pl-1.5 pr-1.5 font-sans text-xs text-n-80 shadow-hairline",
          isUploadInFlight && "animate-pulse",
          hasError &&
            "bg-[color-mix(in_oklab,var(--spectrum-red)_10%,var(--card))] shadow-[0_0_0_1px_color-mix(in_oklab,var(--spectrum-red)_45%,transparent)]",
        )}
      >
        {isImage && previewUrl && !isUploadInFlight ? (
          <img
            src={previewUrl}
            alt={file.name}
            className="size-[22px] shrink-0 rounded-xs object-cover"
          />
        ) : (
          <FileTypeMark
            ext={fileExtension.slice(0, 4)}
            tint={tintForExt(fileExtension)}
          />
        )}
        <span className="max-w-[220px] truncate font-medium">{baseName}</span>
        {fileSize > 0 && (
          <span className="font-mono text-2xs text-n-50">
            {formatFileSize(fileSize)}
          </span>
        )}
        {hasSizeError && (
          <span
            className="inline-flex items-center"
            title={`File larger than ${maxSizeMB}MB cap`}
          >
            <AlertTriangle className="size-3 text-spectrum-red" />
          </span>
        )}
        {file.status === "failed" && !hasSizeError && (
          <span className="inline-flex items-center" title="Upload failed">
            <AlertTriangle className="size-3 text-spectrum-red" />
          </span>
        )}
        {typeof onRemoveFile === "function" && (
          <button
            type="button"
            className="inline-flex size-[18px] items-center justify-center rounded-xs text-n-40 outline-none transition-colors hover:bg-n-10 hover:text-n-80 focus-visible:outline-2 focus-visible:outline-rust"
            onClick={() => onRemoveFile(file)}
            aria-label="Remove file"
          >
            <X className="size-3" />
          </button>
        )}
      </span>
    );
  },
);
